import { useEffect, useRef, useState } from "react";
import { Accordion, Button, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { CompanyType } from "../../models/company";

interface AutomationServiceConfigProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  activeAutomations: string[];
  availableAutomations: string[];
  automationStatuses: string[];
  hasAutomationChanges: (automationType: string) => boolean;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  handleServiceConfigChange: (serviceName: string, path: string[], value: any) => void;
  handleAutomationConfigChange: (automationType: string, field: string, value: any) => void;
  handleAddAutomation: (automationType: string) => void;
  onDeleteAutomation: (automationType: string) => void;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
}

function AutomationServiceConfig({
  company,
  originalCompany,
  activeAutomations,
  automationStatuses,
  hasAutomationChanges,
  hasFieldChanged,
  handleServiceConfigChange,
  handleAutomationConfigChange,
  onDeleteAutomation,
  ChangedIndicator,
  readOnly = false,
}: AutomationServiceConfigProps) {
  const serviceConfig = company?.services?.automation || {};
  const originalServiceConfig = originalCompany?.services?.automation || {};

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const hasGenericConfigChanges = hasFieldChanged(serviceConfig.generic_config, originalServiceConfig.generic_config);

  useEffect(() => {
    if (activeKey && accordionRefs.current[activeKey]) {
      // Small delay to ensure accordion has expanded
      setTimeout(() => {
        accordionRefs.current[activeKey]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 150);
    }
  }, [activeKey]);

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case "live":
        return "bg-success";
      case "colibry-testing":
        return "bg-info";
      case "customer-testing":
        return "bg-warning";
      default:
        return "bg-secondary";
    }
  };

  const renderAutomationFields = (automationType: string) => {
    const automationConfig = company?.services?.automation?.specific_configs?.[automationType] || {};
    const originalAutomationConfig = originalCompany?.services?.automation?.specific_configs?.[automationType] || {};
    const currentStatus = automationConfig.status || "disabled";

    return (
      <>
        <Form.Group className="mb-4">
          <Form.Label>
            Status
            {hasFieldChanged(automationConfig.status, originalAutomationConfig.status) && <ChangedIndicator />}
          </Form.Label>
          <Form.Select
            value={currentStatus}
            onChange={(e) => handleAutomationConfigChange(automationType, "status", e.target.value)}
            disabled={readOnly}
          >
            {automationStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {currentStatus !== "disabled" && (
          <>
            <Form.Group className="mb-4">
              <Form.Label>
                Start Date (Optional)
                {hasFieldChanged(automationConfig.start_date, originalAutomationConfig.start_date) && (
                  <ChangedIndicator />
                )}
              </Form.Label>
              <Form.Control
                type="date"
                value={automationConfig.start_date || ""}
                onChange={(e) => handleAutomationConfigChange(automationType, "start_date", e.target.value)}
                readOnly={readOnly}
              />
            </Form.Group>

            {(automationType === "visit" || automationType === "inboundlead") && (
              <Form.Group className="mb-4">
                <Form.Label>
                  Tasks Older Than{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        The tasks picked up in this automation should not have been updated in the timeframe below.
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle" style={{ cursor: "pointer", color: "#6c757d" }}></i>
                  </OverlayTrigger>
                  {hasFieldChanged(automationConfig.tasks_older_than, originalAutomationConfig.tasks_older_than) && (
                    <ChangedIndicator />
                  )}
                </Form.Label>
                <Row>
                  <Col md={6}>
                    <Form.Control
                      type="number"
                      value={automationConfig.tasks_older_than?.amount || ""}
                      onChange={(e) => {
                        const amount = parseInt(e.target.value);
                        const currentUnit = automationConfig.tasks_older_than?.unit || "hours";
                        handleAutomationConfigChange(automationType, "tasks_older_than", {
                          amount: isNaN(amount) ? null : amount,
                          unit: currentUnit,
                        });
                      }}
                      placeholder="Amount"
                      readOnly={readOnly}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Select
                      value={automationConfig.tasks_older_than?.unit || "hours"}
                      onChange={(e) => {
                        const currentAmount = automationConfig.tasks_older_than?.amount;
                        handleAutomationConfigChange(automationType, "tasks_older_than", {
                          amount: currentAmount,
                          unit: e.target.value,
                        });
                      }}
                      disabled={readOnly}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </Form.Select>
                  </Col>
                </Row>
              </Form.Group>
            )}

            {automationType === "mariage" && (
              <div>
                <h5 className="mb-3">Mariage Configuration</h5>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={
                      <>
                        Include Projects
                        {hasFieldChanged(
                          automationConfig.include_projects,
                          originalAutomationConfig.include_projects,
                        ) && <ChangedIndicator />}
                      </>
                    }
                    checked={automationConfig.include_projects || false}
                    onChange={(e) => handleAutomationConfigChange(automationType, "include_projects", e.target.checked)}
                    disabled={readOnly}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={
                      <>
                        Receive All{" "}
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip>
                              You can maintain "Mariage Receive-All e-mail addresses". These e-mail addresses will
                              receive the mariage mails for all listings, regardless of their search criteria. They do
                              need a Search Criteria to be maintained though (it should contain at least one postal code
                              and should have been updated in the past year).
                            </Tooltip>
                          }
                        >
                          <i className="bi bi-info-circle" style={{ cursor: "pointer", color: "#6c757d" }}></i>
                        </OverlayTrigger>
                        {hasFieldChanged(automationConfig.receive_all, originalAutomationConfig.receive_all) && (
                          <ChangedIndicator />
                        )}
                      </>
                    }
                    checked={Array.isArray(automationConfig.receive_all) && automationConfig.receive_all.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleAutomationConfigChange(automationType, "receive_all", []);
                      } else {
                        handleAutomationConfigChange(automationType, "receive_all", undefined);
                      }
                    }}
                    disabled={readOnly}
                  />
                  {Array.isArray(automationConfig.receive_all) && (
                    <Form.Group className="mt-2">
                      <Form.Label>Email Addresses (one per line)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={(automationConfig.receive_all || []).join("\n")}
                        onChange={(e) => {
                          const emails = e.target.value
                            .split("\n")
                            .map((email: string) => email.trim())
                            .filter((email: string) => email.length > 0);
                          handleAutomationConfigChange(automationType, "receive_all", emails);
                        }}
                        placeholder="email1@example.com&#10;email2@example.com"
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  )}
                </Form.Group>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Location Range (%)
                        {hasFieldChanged(automationConfig.location_range, originalAutomationConfig.location_range) && (
                          <ChangedIndicator />
                        )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={automationConfig.location_range || ""}
                        onChange={(e) =>
                          handleAutomationConfigChange(
                            automationType,
                            "location_range",
                            parseFloat(e.target.value) || "",
                          )
                        }
                        placeholder="e.g., 0.07"
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Price Range (%)
                        {hasFieldChanged(automationConfig.price_range, originalAutomationConfig.price_range) && (
                          <ChangedIndicator />
                        )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={automationConfig.price_range || ""}
                        onChange={(e) =>
                          handleAutomationConfigChange(automationType, "price_range", parseFloat(e.target.value) || "")
                        }
                        placeholder="e.g., 0.15"
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Surface Range (%)
                        {hasFieldChanged(automationConfig.surface_range, originalAutomationConfig.surface_range) && (
                          <ChangedIndicator />
                        )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={automationConfig.surface_range || ""}
                        onChange={(e) =>
                          handleAutomationConfigChange(
                            automationType,
                            "surface_range",
                            parseFloat(e.target.value) || "",
                          )
                        }
                        placeholder="e.g., 0.1"
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Batch Size
                        {hasFieldChanged(automationConfig.batch_size, originalAutomationConfig.batch_size) && (
                          <ChangedIndicator />
                        )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={automationConfig.batch_size || ""}
                        onChange={(e) =>
                          handleAutomationConfigChange(automationType, "batch_size", parseInt(e.target.value) || "")
                        }
                        placeholder="e.g., 1000"
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Listings Per Mail
                    {hasFieldChanged(
                      automationConfig.listings_per_mail,
                      originalAutomationConfig.listings_per_mail,
                    ) && <ChangedIndicator />}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={automationConfig.listings_per_mail || ""}
                    onChange={(e) =>
                      handleAutomationConfigChange(automationType, "listings_per_mail", parseInt(e.target.value) || "")
                    }
                    placeholder="e.g. 5"
                    readOnly={readOnly}
                  />
                </Form.Group>
              </div>
            )}
          </>
        )}

        {!readOnly && (
          <div className="d-flex justify-content-end mt-4">
            <Button variant="danger" size="sm" onClick={() => onDeleteAutomation(automationType)}>
              <i className="bi bi-trash me-2"></i>
              Delete {automationType.charAt(0).toUpperCase() + automationType.slice(1)} Automation
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <style>
        {`
          .automation-config-accordion .accordion-button.collapsed {
            background-color: #f0f8ff;
          }
        `}
      </style>
      <Accordion defaultActiveKey="generic-config" className="mb-3 automation-config-accordion">
        <Accordion.Item eventKey="generic-config">
          <Accordion.Header>
            <div className="d-flex justify-content-between align-items-center w-100 me-2">
              <span>
                Generic Configuration
                {hasGenericConfigChanges && <ChangedIndicator />}
              </span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Customer Testing Email{" "}
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip>If an automation is in customer testing, mails will be sent to this address.</Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle" style={{ cursor: "pointer", color: "#6c757d" }}></i>
                    </OverlayTrigger>
                    {hasFieldChanged(
                      serviceConfig.generic_config?.customer_testing_email,
                      originalServiceConfig.generic_config?.customer_testing_email,
                    ) && <ChangedIndicator />}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={serviceConfig.generic_config?.customer_testing_email || ""}
                    onChange={(e) =>
                      handleServiceConfigChange(
                        "automation",
                        ["generic_config", "customer_testing_email"],
                        e.target.value,
                      )
                    }
                    readOnly={readOnly}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label={
                    <>
                      Differentiate Projects{" "}
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip>
                            Should we distinguish between projects and non-projects? E.g. If you differentiate, some
                            mails would follow the VISIT_SALE flow, while others would follow the VISIT_SALE_NEW flow.
                            If you don't differentiate, all would follow the VISIT_SALE flow. Click the info icon for
                            more information.
                          </Tooltip>
                        }
                      >
                        <i className="bi bi-info-circle" style={{ cursor: "pointer", color: "#6c757d" }}></i>
                      </OverlayTrigger>
                      {hasFieldChanged(
                        serviceConfig.generic_config?.differentiate_projects,
                        originalServiceConfig.generic_config?.differentiate_projects,
                      ) && <ChangedIndicator />}
                    </>
                  }
                  checked={serviceConfig.generic_config?.differentiate_projects || false}
                  onChange={(e) =>
                    handleServiceConfigChange(
                      "automation",
                      ["generic_config", "differentiate_projects"],
                      e.target.checked,
                    )
                  }
                  disabled={readOnly}
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label={
                    <>
                      Generate ICS File{" "}
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip>
                            Add the appointment as a calendar invite in the email (only relevant for confirmations and
                            reminders).
                          </Tooltip>
                        }
                      >
                        <i className="bi bi-info-circle" style={{ cursor: "pointer", color: "#6c757d" }}></i>
                      </OverlayTrigger>
                      {hasFieldChanged(
                        serviceConfig.generic_config?.generate_ics_file,
                        originalServiceConfig.generic_config?.generate_ics_file,
                      ) && <ChangedIndicator />}
                    </>
                  }
                  checked={serviceConfig.generic_config?.generate_ics_file !== false}
                  onChange={(e) =>
                    handleServiceConfigChange("automation", ["generic_config", "generate_ics_file"], e.target.checked)
                  }
                  disabled={readOnly}
                />
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Accordion
        className="automation-config-accordion"
        onSelect={(key) => setActiveKey(key as string | null)}
        style={{ marginBottom: "30px" }}
      >
        {activeAutomations.map((automationType) => {
          const automationConfig = company?.services?.automation?.specific_configs?.[automationType] || {};
          const currentStatus = automationConfig.status || "disabled";

          return (
            <Accordion.Item
              eventKey={automationType}
              key={automationType}
              ref={(el: HTMLDivElement | null) => {
                accordionRefs.current[automationType] = el;
              }}
            >
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 me-2">
                  <span className="text-capitalize">
                    {automationType}
                    {hasAutomationChanges(automationType) && <ChangedIndicator />}
                  </span>
                  <span className={`badge ${statusBadgeClass(currentStatus)}`}>{currentStatus}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>{renderAutomationFields(automationType)}</Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </>
  );
}

export default AutomationServiceConfig;
